import React, { useState } from 'react';
import { Formik, FormikProps } from 'formik';
import { Form, Input, Select } from 'formik-antd';
import * as Yup from 'yup';
import { Col, Row } from 'antd';
import { RadioChangeEvent } from 'antd/lib/radio';

import Radio from 'components/formik/Radio';
import FormikEffect from 'components/formik/FormikEffect';
import FormItem from 'components/formik/FormItem';
import { useAppSelector } from 'hooks/useAppSelector';
import { CustomerPreScreeningProviderEnum } from 'api/adminApiClient';
import {
  getCustomerBreakInServiceEnumOptions,
  getCustomerPreScreeningBilledLabel,
} from 'utils/clientUtils';
import { urlRegex } from 'utils/urlUtils';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import {
  BreakInServiceEnum,
  ResourceDownloadResponse,
} from 'api/backofficeApiClient';
import OnboardingDocumentationUploader from './OnboardingDocumentationUploader';
import {
  assignmentRequirementLongTextMaxLength,
  mediumCharactersLength,
} from 'constants/index';

type Props = {
  formRef: any;
  onChange: (formik: FormikProps<AssignmentRequirementsFormValues>) => void;
  onboardingDocumentationFileInfo?: ResourceDownloadResponse[];
  initialValues?: AssignmentRequirementsFormValues;
};

export interface AssignmentRequirementsFormValues {
  contractorsPermitted?: boolean;
  preScreeningRequired?: boolean;
  preScreeningBilledTo?: CustomerPreScreeningProviderEnum;
  preScreening: string;
  designatedPreScreening?: boolean;
  isOnboardingDocumentationRequired?: boolean;
  specialInstructions: string;
  onboardingDocumentation: string[];
  workPerformance: string;
  timeEntry: string;
  assignmentNotes: string;
  breakInService?: BreakInServiceEnum;
  isDrugScreensRequired?: boolean;
  drugScreenRequirements?: string;
  isCredentialingRequired?: boolean;
  credentialingRequirements?: string;
  isSpecialRatesDefined?: boolean;
  specialRateRequirements?: string;
  cspNotes?: string;
}

const validationSchema = Yup.object({
  contractorsPermitted: Yup.boolean(),
  preScreeningRequired: Yup.boolean(),
  preScreeningBilledTo: Yup.number(),
  preScreening: Yup.string()
    .trim()
    .max(
      120,
      `Contract background screen requirements should be 120 characters maximum`,
    ),
  designatedPreScreening: Yup.boolean(),
  isOnboardingDocumentationRequired: Yup.boolean(),
  specialInstructions: Yup.string()
    .trim()
    .max(
      mediumCharactersLength,
      `Special instructions should be ${mediumCharactersLength} characters maximum`,
    ),
  onboardingDocumentation: Yup.array().of(Yup.string()),
  workPerformance: Yup.string()
    .trim()
    .max(
      mediumCharactersLength,
      `Guaranteed work performance should be ${mediumCharactersLength} characters maximum`,
    ),
  timeEntry: Yup.string()
    .trim()
    .max(120, `Time entry method should be 120 characters maximum`),
  assignmentNotes: Yup.string()
    .trim()
    .max(
      mediumCharactersLength,
      `Timecard special instructions should be ${mediumCharactersLength} characters maximum`,
    ),
  breakInService: Yup.number().nullable(),
  isDrugScreensRequired: Yup.boolean(),
  drugScreenRequirements: Yup.string()
    .trim()
    .max(
      assignmentRequirementLongTextMaxLength,
      `Drug screen requirements should be ${assignmentRequirementLongTextMaxLength} characters maximum`,
    ),
  isCredentialingRequired: Yup.boolean(),
  credentialingRequirements: Yup.string()
    .trim()
    .max(
      assignmentRequirementLongTextMaxLength,
      `Credentialing requirements should be ${assignmentRequirementLongTextMaxLength} characters maximum`,
    ),
  isSpecialRatesDefined: Yup.boolean(),
  specialRateRequirements: Yup.string()
    .trim()
    .max(
      assignmentRequirementLongTextMaxLength,
      `Special rate requirements should be ${assignmentRequirementLongTextMaxLength} characters maximum`,
    ),
  cspNotes: Yup.string()
    .trim()
    .max(
      assignmentRequirementLongTextMaxLength,
      `CSP notes should be ${assignmentRequirementLongTextMaxLength} characters maximum`,
    )
    .matches(urlRegex, {
      message: 'CSP notes URL format is invalid',
      excludeEmptyString: true,
    }),
});

const preScreeningBilledMemberLabel = getCustomerPreScreeningBilledLabel(
  CustomerPreScreeningProviderEnum.Member,
);
const preScreeningBilledMBPLabel = getCustomerPreScreeningBilledLabel(
  CustomerPreScreeningProviderEnum.MBP,
);

const preScreeningBilledCustomerLabel = getCustomerPreScreeningBilledLabel(
  CustomerPreScreeningProviderEnum.Customer,
);

export default function AssignmentRequirements(props: Props) {
  const {
    formRef,
    initialValues: predefinedInitialValues,
    onChange,
    onboardingDocumentationFileInfo: defaultOnboardingDocumentationFileInfo,
  } = props;
  const initialValues: AssignmentRequirementsFormValues = {
    contractorsPermitted: undefined,
    preScreeningRequired: undefined,
    preScreeningBilledTo: undefined,
    preScreening: '',
    designatedPreScreening: undefined,
    isOnboardingDocumentationRequired: undefined,
    specialInstructions: '',
    onboardingDocumentation: [],
    workPerformance: '',
    timeEntry: '',
    assignmentNotes: '',
    breakInService: undefined,
    isDrugScreensRequired: undefined,
    drugScreenRequirements: '',
    isCredentialingRequired: undefined,
    credentialingRequirements: '',
    isSpecialRatesDefined: undefined,
    specialRateRequirements: '',
    cspNotes: '',
  };
  const [onboardingDocumentationFileInfo, setOnboardingDocumentationFileInfo] =
    useState(defaultOnboardingDocumentationFileInfo);
  const isAdminRole = useAppSelector(isAdminSelector);

  const clearConditionalField = ({
    event,
    formik,
    fieldName,
  }: {
    event: RadioChangeEvent;
    formik: FormikProps<AssignmentRequirementsFormValues>;
    fieldName:
      | 'drugScreenRequirements'
      | 'credentialingRequirements'
      | 'specialRateRequirements';
  }) => {
    if (!event.target.value) {
      formik.setFieldValue(fieldName, '', false);
    }
  };

  const handlePreScreeningChange = (
    ev: RadioChangeEvent,
    formik: FormikProps<AssignmentRequirementsFormValues>,
  ) => {
    if (ev.target.value) {
      formik.setFieldValue(
        'preScreeningBilledTo',
        CustomerPreScreeningProviderEnum.Member,
        false,
      );

      return;
    }

    formik.setFieldValue('preScreeningBilledTo', undefined, false);
  };

  const handleOnboardingDocumentationChange = (
    ev: RadioChangeEvent,
    formik: FormikProps<AssignmentRequirementsFormValues>,
  ) => {
    if (!ev.target.value) {
      formik.setFieldValue('specialInstructions', '', false);
      formik.setFieldValue('onboardingDocumentation', [], false);

      setOnboardingDocumentationFileInfo([]);
    }
  };

  const handleDrugScreensChange = (
    ev: RadioChangeEvent,
    formik: FormikProps<AssignmentRequirementsFormValues>,
  ) => {
    clearConditionalField({
      event: ev,
      formik,
      fieldName: 'drugScreenRequirements',
    });
  };

  const handleCredentialingChange = (
    ev: RadioChangeEvent,
    formik: FormikProps<AssignmentRequirementsFormValues>,
  ) => {
    clearConditionalField({
      event: ev,
      formik,
      fieldName: 'credentialingRequirements',
    });
  };

  const handleSpecialRatesChange = (
    ev: RadioChangeEvent,
    formik: FormikProps<AssignmentRequirementsFormValues>,
  ) => {
    clearConditionalField({
      event: ev,
      formik,
      fieldName: 'specialRateRequirements',
    });
  };

  return (
    <Formik
      enableReinitialize
      innerRef={formRef}
      initialValues={predefinedInitialValues || initialValues}
      validationSchema={validationSchema}
      onSubmit={() => {}}
    >
      {(formik) => (
        <>
          <FormikEffect onChange={onChange} />

          <Form layout="vertical">
            <div className="modal-title">Assignment requirements</div>
            <Row gutter={40} align="top">
              <Col xs={24} md={12}>
                <FormItem
                  name="contractorsPermitted"
                  label="Are independent contractors or subcontractors permitted?"
                  data-id="editCustomerContractorsPermittedField"
                >
                  <Radio.Group
                    className="radio"
                    name="contractorsPermitted"
                    options={[
                      {
                        label: 'Yes',
                        value: true,
                      },
                      {
                        label: 'No',
                        value: false,
                      },
                    ]}
                  />
                </FormItem>

                <FormItem
                  name="preScreeningRequired"
                  label="Pre-screening required"
                  data-id="editCustomerPreScreeningRequiredField"
                  className="inline-radio"
                >
                  <Radio.Group
                    className="radio"
                    name="preScreeningRequired"
                    onChange={(ev) => handlePreScreeningChange(ev, formik)}
                    options={[
                      {
                        label: 'Yes',
                        value: true,
                      },
                      {
                        label: 'No',
                        value: false,
                      },
                    ]}
                  />
                </FormItem>

                <FormItem
                  name="preScreening"
                  label="Contract background screen requirements"
                  data-id="editCustomerPreScreeningField"
                >
                  <Input
                    name="preScreening"
                    placeholder="Enter requirements"
                    autoComplete="off"
                  />
                </FormItem>

                {isAdminRole && (
                  <FormItem
                    name="cspNotes"
                    label="CSP notes"
                    data-id="editCustomerCspNotesField"
                  >
                    <Input
                      name="cspNotes"
                      placeholder="Enter URL"
                      autoComplete="off"
                    />
                  </FormItem>
                )}

                <FormItem
                  name="isOnboardingDocumentationRequired"
                  label="Onboarding documentation required"
                  data-id="editCustomerOnboardingDocumentationField"
                >
                  <Radio.Group
                    className="radio"
                    name="isOnboardingDocumentationRequired"
                    onChange={(ev) =>
                      handleOnboardingDocumentationChange(ev, formik)
                    }
                    options={[
                      {
                        label: 'Yes',
                        value: true,
                      },
                      {
                        label: 'No',
                        value: false,
                      },
                    ]}
                  />
                </FormItem>

                <FormItem
                  name="specialInstructions"
                  label="Special instructions"
                  data-id="editCustomerSpecialInstructionsField"
                >
                  <Input
                    name="specialInstructions"
                    disabled={!formik.values.isOnboardingDocumentationRequired}
                    placeholder="Enter special instructions"
                    autoComplete="off"
                  />
                </FormItem>

                {formik.values.isOnboardingDocumentationRequired && (
                  <div className="upload-wrapper">
                    <FormItem
                      name="onboardingDocumentation"
                      label="Onboarding documentation"
                      data-id="editCustomerOnboardingDocumentationField"
                      showValidateError={false}
                    >
                      <OnboardingDocumentationUploader
                        fieldName="onboardingDocumentation"
                        files={onboardingDocumentationFileInfo}
                      />
                    </FormItem>
                  </div>
                )}
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  name="designatedPreScreening"
                  label="Designated pre-screening provider"
                  data-id="editCustomerDesignatedPreScreeningField"
                >
                  <Radio.Group
                    className="radio"
                    name="designatedPreScreening"
                    options={[
                      {
                        label: 'Yes',
                        value: true,
                      },
                      {
                        label: 'No',
                        value: false,
                      },
                    ]}
                  />
                </FormItem>

                <FormItem
                  name="preScreeningBilledTo"
                  label="Pre-screening billed to"
                  data-id="editCustomerPreScreeningBilledToField"
                >
                  <Radio.Group
                    className="radio"
                    name="preScreeningBilledTo"
                    disabled={!formik.values.preScreeningRequired}
                    options={[
                      {
                        label: preScreeningBilledMemberLabel,
                        value: CustomerPreScreeningProviderEnum.Member,
                      },
                      {
                        label: preScreeningBilledCustomerLabel,
                        value: CustomerPreScreeningProviderEnum.Customer,
                      },
                      {
                        label: preScreeningBilledMBPLabel,
                        value: CustomerPreScreeningProviderEnum.MBP,
                      },
                    ]}
                  />
                </FormItem>

                <FormItem
                  name="isDrugScreensRequired"
                  label="Drug screens required?"
                  className="inline-radio"
                >
                  <Radio.Group
                    className="radio"
                    name="isDrugScreensRequired"
                    onChange={(ev) => handleDrugScreensChange(ev, formik)}
                    options={[
                      { label: 'Yes', value: true },
                      { label: 'No', value: false },
                    ]}
                  />
                </FormItem>

                {formik.values.isDrugScreensRequired && (
                  <FormItem
                    name="drugScreenRequirements"
                    label="Drug screen requirements"
                  >
                    <Input.TextArea
                      name="drugScreenRequirements"
                      placeholder="Enter drug screen requirements"
                      autoComplete="off"
                      rows={4}
                    />
                  </FormItem>
                )}

                <FormItem
                  name="isCredentialingRequired"
                  label="Is credentialing required?"
                  className="inline-radio"
                >
                  <Radio.Group
                    className="radio"
                    name="isCredentialingRequired"
                    onChange={(ev) => handleCredentialingChange(ev, formik)}
                    options={[
                      { label: 'Yes', value: true },
                      { label: 'No', value: false },
                    ]}
                  />
                </FormItem>

                {formik.values.isCredentialingRequired && (
                  <FormItem
                    name="credentialingRequirements"
                    label="Credentialing requirements"
                  >
                    <Input.TextArea
                      name="credentialingRequirements"
                      placeholder="Enter credentialing requirements"
                      autoComplete="off"
                      rows={4}
                    />
                  </FormItem>
                )}

                <FormItem
                  name="isSpecialRatesDefined"
                  label="Special rates defined?"
                  className="inline-radio"
                >
                  <Radio.Group
                    className="radio"
                    name="isSpecialRatesDefined"
                    onChange={(ev) => handleSpecialRatesChange(ev, formik)}
                    options={[
                      { label: 'Yes', value: true },
                      { label: 'No', value: false },
                    ]}
                  />
                </FormItem>

                {formik.values.isSpecialRatesDefined && (
                  <FormItem
                    name="specialRateRequirements"
                    label="Special rate requirements"
                  >
                    <Input.TextArea
                      name="specialRateRequirements"
                      placeholder="Enter special rate requirements"
                      autoComplete="off"
                      rows={4}
                    />
                  </FormItem>
                )}
              </Col>
            </Row>

            <div className="modal-title">Special assignment requirements</div>

            <Row gutter={40}>
              <Col xs={24} md={12}>
                <FormItem
                  name="workPerformance"
                  label="Guaranteed work performance"
                  data-id="editCustomerWorkPerformanceField"
                >
                  <Input
                    name="workPerformance"
                    placeholder="Enter guaranteed work performance"
                    autoComplete="off"
                  />
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  name="timeEntry"
                  label="Time entry method"
                  data-id="editCustomerTimeEntryField"
                >
                  <Input
                    name="timeEntry"
                    placeholder="Enter time entry method"
                    autoComplete="off"
                  />
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  name="assignmentNotes"
                  label="Timecard special instructions"
                  data-id="editCustomerAssignmentNotesField"
                >
                  <Input
                    name="assignmentNotes"
                    placeholder="Enter timecard special instructions"
                    autoComplete="off"
                  />
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  name="breakInService"
                  label="Break in service"
                  data-id="editBreakInServiceField"
                >
                  <Select
                    name="breakInService"
                    placeholder="Select break in service"
                  >
                    {getCustomerBreakInServiceEnumOptions().map((item) => (
                      <Select.Option key={item.value} value={item.value}>
                        {item.label}
                      </Select.Option>
                    ))}
                  </Select>
                </FormItem>
              </Col>
            </Row>
          </Form>
        </>
      )}
    </Formik>
  );
}
